class StoreLocator {
  constructor(sectionId, config) {
    this.sectionId = sectionId;
    this.config = config;
    this.map = null;
    this.markers = [];
    this.markerCluster = null;
    this.autocomplete = null;
    this.userPosition = null;
    this.showingAllStores = false;
    this.allStores = [];
    this.nearbyStores = [];
    this.visibleStores = [];
    this.infoWindow = null;
    this.backendUrl =
      "https://store-locator-shopify-app-d9f525452f2f.herokuapp.com";
    this.userMarker = null;
    this.searchMarker = null;
    this.selectedProvinces = [];
    this.manualStoreColor = config.manual_store_color || "#FF0000";
    this.searchTerm = "";
    this.setupSearch();
    this.provinceFilterOpen = false;
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.preloader = document.getElementById(`preloader-${sectionId}`);
    this.wrapper = document.querySelector(
      `#store-locator-${sectionId} .store-locator-wrapper`
    );

    this.setHeights();
    window.addEventListener("resize", () => this.setHeights());

   
    this.setupSearch()
    this.init();
  }

  setHeights() {
    const mapElement = document.getElementById(`map-${this.sectionId}`);
    const resultsContainer = document.querySelector(
      `#results-column-${this.sectionId} .stores-scroll-container`
    );

    if (window.innerWidth <= 768) {
      // Mobile layout
      if (mapElement) {
        mapElement.style.height = "50vh";
      }
      if (resultsContainer) {
        resultsContainer.style.maxHeight = "50vh";
      }
    } else {
      // Desktop layout - match heights
      if (mapElement && resultsContainer) {
        const mapHeight = mapElement.offsetHeight;
        resultsContainer.style.maxHeight = `${mapHeight}px`;
      }
    }
  }

  async init() {
    try {
      performance.mark("locator_start");

      await this.loadGoogleMaps();

      // Initialize map first, then load stores
      await this.initMap();
      await this.refreshStores();

      this.initAutocomplete();
      this.setupEventListeners();
      this.setupBrandSelector();

      const scrollContainer = document.querySelector(
        `#results-column-${this.sectionId} .stores-scroll-container`
      );
      if (scrollContainer) {
        this.resizeObserver.observe(scrollContainer);
      }
    } catch (error) {
      console.error("Initialization failed:", error);
      this.showMapError(this.sectionId, "Failed to initialize store locator");
    } finally {
      this.hideLoader();
    }
  }

  handleResize() {
    this.setHeights();
    this.updateVisibleStores();
  }

  hideLoader() {
    if (this.preloader) {
      performance.mark("locator_loaded");
      performance.measure(
        "locator_load_time",
        "locator_start",
        "locator_loaded"
      );
      const duration =
        performance.getEntriesByName("locator_load_time")[0]?.duration;

      this.preloader.style.opacity = "0";

      if (this.wrapper) {
        this.wrapper.style.opacity = "1";
        this.wrapper.style.visibility = "visible";
      }

      setTimeout(() => {
        document.body.classList.add("locator-loaded");
        this.preloader.style.display = "none";
      }, 300);
    }
  }

  async loadGoogleMaps() {
    return new Promise((resolve, reject) => {
      if (window.google && google.maps) return resolve();

      const callbackName = `gmapsCallback_${Date.now()}`;
      const script = document.createElement("script");

      script.loading = "async";

      script.src = `${this.backendUrl}/maps-api?brand=${this.config.brand}&callback=${callbackName}&libraries=places,geometry`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        document.head.removeChild(script);
        reject(new Error("Failed to load Google Maps API"));
      };

      window[callbackName] = () => {
        delete window[callbackName];
        if (!window.google || !google.maps) {
          reject(new Error("Google Maps library not properly loaded"));
        } else {
          resolve();
        }
      };

      document.head.appendChild(script);

      // Fallback timeout
      setTimeout(() => {
        if (!window.google) {
          reject(new Error("Google Maps loading timed out"));
        }
      }, 20000);
    });
  }

  async handleLocationUpdate(position, isSearch = false) {
    if (!position || !this.map) return;
    this.showResultsLoader();
    
    try {
        this.userPosition = position;
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
        
        if (this.userMarker) this.userMarker.map = null;
        if (this.searchMarker) this.searchMarker.map = null;
        
        const newMarker = new AdvancedMarkerElement({
            position: this.userPosition,
            map: this.map,
            title: isSearch ? "Searched Location" : "You are here",
            content: isSearch ? undefined : this.createCustomMarkerElement("green"),
        });

        if (isSearch) this.searchMarker = newMarker;
        else this.userMarker = newMarker;

        // Recalculate distances for all stores and get the sorted list
        await this.processStores();

        // Display the 5 closest stores in the list
        this.visibleStores = this.nearbyStores.slice(0, 5);
        this.displayStores();

        // Fit map bounds to the user and the 5 closest stores
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(this.userPosition);
        this.visibleStores.forEach(store => {
            bounds.extend({ lat: parseFloat(store.latitude), lng: parseFloat(store.longitude) });
        });
        this.map.fitBounds(bounds, window.innerWidth <= 768 ? 50 : 100);
        if (this.map.getZoom() > 14) this.map.setZoom(14);

    } catch (error) {
        console.error("Error updating location:", error);
    } finally {
        this.hideResultsLoader();
    }
  }

  async initMap() {
    try {
      const { Map, InfoWindow } = await google.maps.importLibrary("maps");
      const mapElement = document.getElementById(`map-${this.sectionId}`);

      // Default center (centered to include both South Africa and Lesotho)
      const defaultCenter = { lat: -29.0, lng: 26.5 };
      this.map = new Map(mapElement, {
        center: defaultCenter,
        zoom: 5,
        mapId: "YOUR_MAP_ID_HERE",
        // --- START: Added for intuitive map interaction ---
        gestureHandling: "cooperative",
        // --- END: Added for intuitive map interaction ---
      });

      // --- START: Added for smooth map loading ---
      // Fade map in smoothly when ready
      google.maps.event.addListenerOnce(this.map, "tilesloaded", () => {
        if (mapElement) {
          mapElement.classList.add("loaded");
        }
      });
      // --- END: Added for smooth map loading ---

      // --- START: MODIFICATION - Removed automatic geolocation on load ---
      // Geolocation is now only triggered by the "Use My Location" button.
      // this.userPosition is initialized as null, so all stores will be shown by default.
      // --- END: MODIFICATION ---

      this.map.addListener("bounds_changed", () => {
        this.updateVisibleStores();
      });

      this.map.addListener("zoom_changed", () => {
        this.updateVisibleStores();
      });

      // Don't set bounds here - let the stores determine the bounds when they're loaded
    } catch (error) {
      console.error("Map initialization failed:", error);
      this.showMapError();
    }
  }

  createCustomMarkerElement(color) {
    const element = document.createElement("div");
    element.innerHTML = `
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" 
                      d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z" 
                      fill="${color}"/>
              </svg>
            `;
    return element;
  }

  async centerMapOnUser(position) {
    if (!position || !this.map) return;

    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

    if (this.userMarker) {
      this.userMarker.map = null;
    }

    this.userPosition = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    };

    this.map.setCenter(this.userPosition);
    this.map.setZoom(13);

    this.userMarker = new AdvancedMarkerElement({
      position: this.userPosition,
      map: this.map,
      title: "You are here",
      content: this.createCustomMarkerElement("green"),
    });

    this.processStores();
  }

  initAutocomplete() {
    const inputId = `autocomplete-${this.sectionId}`;
    const input = document.getElementById(inputId);

    if (!input) {
      console.error("Autocomplete input not found with ID:", inputId);
      return;
    }

    if (!google.maps.places) {
      console.error("Google Maps Places library not loaded");
      return;
    }

    try {
      this.autocomplete = new google.maps.places.Autocomplete(input, {
        types: ["geocode"],
        componentRestrictions: { country: "za" }, // South Africa restriction
        fields: ["geometry", "name", "formatted_address"],
      });

      this.autocomplete.addListener("place_changed", () => {
        const place = this.autocomplete.getPlace();

        if (!place.geometry || !place.geometry.location) {
          //   console.warn("No geometry for selected place");
          return;
        }

        this.userPosition = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        this.map.setCenter(this.userPosition);
        this.map.setZoom(12);

        // Clear previous search marker if exists
        if (this.searchMarker) {
          this.searchMarker.setMap(null);
        }

        // Add new marker
        this.searchMarker = new google.maps.marker.AdvancedMarkerElement({
          position: this.userPosition,
          map: this.map,
          title: place.name || "Selected Location",
        });

        this.processStores();
      });

      // Prevent form submission when selecting a place
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
        }
      });
    } catch (error) {
      console.error("Autocomplete initialization failed:", error);
    }
  }

  showResultsLoader() {
    const resultsColumn = document.getElementById(
      `results-column-${this.sectionId}`
    );
    if (!resultsColumn) return;

    // Check if the loader already exists, if not, create it once
    let loader = resultsColumn.querySelector(".results-loader");
    if (!loader) {
      loader = document.createElement("div");
      loader.className = "results-loader";
      loader.innerHTML = `
            <svg width="40" height="40" viewBox="0 0 50 50">
              <circle cx="25" cy="25" r="20" fill="none" stroke-width="5" stroke="#ccc"></circle>
              <circle cx="25" cy="25" r="20" fill="none" stroke-width="5"
                      stroke="${this.config.buttonColor || "#082930"}"
                      stroke-linecap="round"
                      transform="rotate(-90 25 25)">
                <animate attributeName="stroke-dasharray" from="0 125.66" to="125.66 0" dur="1.5s" repeatCount="indefinite"></animate>
                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1.5s" repeatCount="indefinite"></animateTransform>
              </circle>
            </svg>
          `;
      resultsColumn.appendChild(loader);
    }

    // Add a class to the parent to show the loader and dim the content
    resultsColumn.classList.add("is-loading");
  }

  hideResultsLoader() {
    const resultsColumn = document.getElementById(
      `results-column-${this.sectionId}`
    );
    if (resultsColumn) {
      resultsColumn.classList.remove("is-loading");
    }
  }

  setupEventListeners() {
    const locationBtn = document.querySelector(
      `.store-locator-location-btn[data-section-id="${this.sectionId}"]`
    );
    const searchIcon = document.querySelector(
      `.store-locator-search-icon[data-section-id="${this.sectionId}"]`
    );
    const addressInput = document.getElementById(
      `autocomplete-${this.sectionId}`
    );

    // Location button
    if (locationBtn) {
      locationBtn.addEventListener("click", () => this.useMyLocation());
    } else {
      console.warn("Location button not found");
    }

    // Search icon
    if (searchIcon) {
      searchIcon.addEventListener("click", () => this.searchByAddress());
    }

    // Also trigger search on Enter key in input field
    if (addressInput) {
      addressInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          this.searchByAddress();
        }
      });
    }
    const toggleBtn = document.querySelector(
      `.province-filter-toggle[data-section-id="${this.sectionId}"]`
    );
    if (toggleBtn) {
      toggleBtn.addEventListener("click", () => {
        this.provinceFilterOpen = !this.provinceFilterOpen;
        const options = toggleBtn.nextElementSibling;
        toggleBtn.setAttribute("aria-expanded", this.provinceFilterOpen);
        options.setAttribute("aria-hidden", !this.provinceFilterOpen);

        options.classList.toggle("visible", this.provinceFilterOpen);
      });
    }

    // Province checkbox changes
    const checkboxes = document.querySelectorAll(
      `.province-checkbox[data-section-id="${this.sectionId}"]`
    );
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (e) => {
        this.selectedProvinces = Array.from(
          document.querySelectorAll(
            `.province-checkbox[data-section-id="${this.sectionId}"]:checked`
          )
        ).map((cb) => cb.value);

        // Apply combined province filter
        this.applyProvinceFilter();
      });
    });
  }

  setupSearch() {
    const searchInput = document.getElementById(
      `store-search-${this.sectionId}`
    );
    const clearButton = document.querySelector(
      `#store-search-${this.sectionId} + .store-search-clear`
    );

    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.searchTerm = e.target.value.toLowerCase().trim();
        this.filterStores();
      });
    }

    if (clearButton) {
      clearButton.addEventListener("click", () => {
        this.searchTerm = "";
        searchInput.value = "";
        this.filterStores();
      });
    }
  }

  filterStores() {
    // Start with all visible stores (already filtered by map bounds and province)
    let filteredStores = [...this.visibleStores];

    // Apply search filter if there's a search term
    if (this.searchTerm) {
      filteredStores = filteredStores.filter((store) => {
        const searchFields = [
          store.name,
          store.address,
          store.contact_number,
          store.province,
        ]
          .join(" ")
          .toLowerCase();
        return searchFields.includes(this.searchTerm);
      });
    }

    // Update the display
    this.displayFilteredStores(filteredStores);
  }

  displayFilteredStores(filteredStores) {
    const container = document.getElementById(
      `nearby-stores-${this.sectionId}`
    );
    if (!container) return;

    container.innerHTML =
      filteredStores.length === 0
        ? '<p class="no-stores-message">No stores match your search</p>'
        : "";

    filteredStores.forEach((store) => {
      // Only create listing if it doesn't already exist
      const storeId = `store-${
        store.id || store.name.toLowerCase().replace(/\s+/g, "-")
      }`;
      if (!document.getElementById(storeId)) {
        this.createStoreListing(store, container);
      }
    });

    // Hide non-matching stores
    container.querySelectorAll(".store-item").forEach((el) => {
      const storeId = el.id.replace("store-", "");
      const shouldShow = filteredStores.some(
        (store) =>
          (store.id || store.name.toLowerCase().replace(/\s+/g, "-")) ===
          storeId
      );
      el.style.display = shouldShow ? "block" : "none";
    });
  }

  applyProvinceFilter() {
    this.showResultsLoader(); // <-- SHOW the loader

    try {
      this.clearMarkers();
      const storesToShow = this.getFilteredStoresBySelection();

      const markerPromises = storesToShow.map((store) =>
        this.createStoreMarker(store).catch((error) => {
          console.error(`Failed to create marker for ${store.name}:`, error);
          return null;
        })
      );

      // Handle all marker creation promises
      Promise.all(markerPromises)
        .then((markers) => {
          this.markers = markers.filter(Boolean);

          // Update clustering
          if (this.markerCluster) {
            this.markerCluster.clearMarkers();
          }

          this.markerCluster = new markerClusterer.MarkerClusterer({
            map: this.map,
            markers: this.markers,
            algorithm: new markerClusterer.SuperClusterAlgorithm({
              radius: 100,
              maxZoom: 15,
            }),
            renderer: {
              render: ({ count, position }) => {
                const color = this.config.buttonColor || "#082930";
                return new google.maps.marker.AdvancedMarkerElement({
                  position,
                  content: this.createClusterMarker(count, color).element,
                });
              },
            },
          });

          // Update the visible stores list
          this.visibleStores = storesToShow;
          this.displayStores();

          // Adjust map view to show the filtered markers
          this.updateMapView();
        })
        .finally(() => {
          this.hideResultsLoader(); // <-- HIDE the loader after everything is done
        });
    } catch (e) {
      // In case of an immediate error, still hide the loader
      console.error("Error applying province filter:", e);
      this.hideResultsLoader();
    }
  }

  updateMapView() {
    if (!this.map || this.visibleStores.length === 0) {
      // If no stores visible, show default view
      if (this.userPosition) {
        this.map.setCenter(this.userPosition);
        this.map.setZoom(12);
      } else {
        // Default center (centered to include both South Africa and Lesotho)
        this.map.setCenter({ lat: -29.0, lng: 26.5 });
        this.map.setZoom(5);
      }
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    this.visibleStores.forEach((store) => {
      bounds.extend(
        new google.maps.LatLng(
          parseFloat(store.latitude),
          parseFloat(store.longitude)
        )
      );
    });

    // Adjust padding based on screen size
    const padding = window.innerWidth <= 768 ? 50 : 100;
    this.map.fitBounds(bounds, padding);

    // Set minimum zoom level
    const zoom = this.map.getZoom();
    if (zoom > 12) this.map.setZoom(12);
  }

  updateVisibleStores() {
    if (!this.map || !this.allStores) return;
    const bounds = this.map.getBounds();
    if (!bounds) return;

    // First, get stores matching the current province/country filters.
    const provinceFilteredStores =
      this.getStoresByProvinceAndCountrySelection();

    // THEN, filter that result by the map's visible area.
    this.visibleStores = provinceFilteredStores.filter((store) => {
      try {
        const storeLatLng = new google.maps.LatLng(
          parseFloat(store.latitude),
          parseFloat(store.longitude)
        );
        const isInBounds = bounds.contains(storeLatLng);
        return isInBounds;
      } catch (error) {
        return false;
      }
    });

    // Apply search filter to the results
    this.filterStores();
  }

  formatStore(store) {
    return {
      ...store,
      id: store.id || `manual_${Math.random().toString(36).substr(2, 9)}`,
      latitude: parseFloat(store.latitude),
      longitude: parseFloat(store.longitude),
      operating_hours: store.operating_hours || store.operatingHours || {},
    };
  }

  // Use a tighter nearby radius to emphasize truly close stores.
  // Caps any configured radius to a maximum of 10km (10000 meters).
  getNearbyRadius() {
    const defaultMax = 10000; // 10km
    const configured = Number(this.config.nearbyRadius) || defaultMax;
    return Math.min(configured, defaultMax);
  }

  async refreshStores() {
    this.clearMarkers();
    this.clearStoreListings();

    try {
      const response = await fetch(
        `${this.backendUrl}/api/stores?brand=${this.config.brand}`
      );
      const dbStores = response.ok ? await response.json() : [];

      // Format stores (simplified - no grouping for Tekkie Town)
      this.allStores = dbStores.map((store) => this.formatStore(store));

      await this.processStores();

      this.updateVisibleStores();
      this.filterStores();
    } catch (error) {
      console.error("Failed to fetch stores:", error);
      this.showStoreLoadError();
    }
  }

   async processStores() {
    if (!this.allStores || this.allStores.length === 0) return;
    this.clearMarkers();

    this.allStores = this.allStores.filter(store => !isNaN(parseFloat(store.latitude)) && !isNaN(parseFloat(store.longitude)));
    
    // Create markers for all currently filtered stores (by province)
    const storesToMark = this.getStoresByProvinceAndCountrySelection();
    const markerPromises = storesToMark.map(store => this.createStoreMarker(store));
    this.markers = (await Promise.all(markerPromises)).filter(Boolean);
    this.refreshCluster();

    // Calculate distances and sort if a user position exists.
    if (this.userPosition) {
        // Calculate distance for ALL stores, regardless of filters
        this.allStores.forEach((store) => {
            const storePos = { lat: parseFloat(store.latitude), lng: parseFloat(store.longitude) };
            store.distance = google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(this.userPosition), new google.maps.LatLng(storePos));
        });
        
        // This is the key fix: Sort ALL stores by distance. Do not filter by radius.
        this.nearbyStores = [...this.allStores].sort((a, b) => a.distance - b.distance);
    } else {
        this.nearbyStores = [...this.allStores];
        this.allStores.forEach((store) => { store.distance = null; });
        
        // On initial load without a user position, fit the map to all markers.
        if (this.markers.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            this.markers.forEach((marker) => bounds.extend(marker.position));
            this.map.fitBounds(bounds, 50);
        }
    }
  }

  async displayStores() {
    const container = document.getElementById(
      `nearby-stores-${this.sectionId}`
    );
    if (!container) {
      console.error("Container not found");
      return;
    }

    // Debug: Force reflow before updating
    void container.offsetHeight;

    container.innerHTML =
      this.visibleStores.length === 0
        ? '<p class="no-stores-message">No stores visible in current view</p>'
        : "";

    // Create store listings
    this.visibleStores.forEach((store) => {
      this.createStoreListing(store, container);
    });

    this.setHeights();
    setTimeout(() => {
      container.style.display = "none";
      void container.offsetHeight;
      container.style.display = "block";
    }, 50);
  }

  filterStoresByProvince() {
     if (this.selectedProvinces.length === 0) {
      return [...this.allStores];
    }

    // This makes it easy to add more countries in the future.
    const countryLevelFilters = ["Namibia", "Lesotho", "Eswatini", "Botswana"];

    // 1. Figure out which countries the user has actually selected.
    const selectedCountries = this.selectedProvinces.filter((selection) =>
      countryLevelFilters.includes(selection)
    );

    // 2. Figure out which real provinces the user has selected.
    const selectedSaProvinces = this.selectedProvinces.filter(
      (selection) => !countryLevelFilters.includes(selection)
    );

    // Now, filter the stores with clear, separate conditions.
    return this.allStores.filter((store) => {
      // Condition 1: Show the store if its country_name is in our list of selected countries.
      // This works for Namibia, Lesotho, etc.
      if (selectedCountries.includes(store.country_name)) {
        return true;
      }

      // Condition 2: Show the store if its province is in our list of selected SA provinces.
      if (selectedSaProvinces.includes(store.province)) {
        return true;
      }

      // If neither condition is met, exclude the store.
      return false;
    });
  }

  resetProvinceFilter() {
    this.selectedProvinces = [];
    const checkboxes = document.querySelectorAll(
      `.province-checkbox[data-section-id="${this.sectionId}"]`
    );
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
  }

  getStoresByProvinceAndCountrySelection() {
    if (this.selectedProvinces.length === 0) {
      return [...this.allStores];
    }

    // This makes it easy to add more countries in the future.
    const countryLevelFilters = ["Namibia", "Lesotho", "Eswatini", "Botswana"];

    // 1. Figure out which countries the user has actually selected.
    const selectedCountries = this.selectedProvinces.filter((selection) =>
      countryLevelFilters.includes(selection)
    );

    // 2. Figure out which real provinces the user has selected.
    const selectedSaProvinces = this.selectedProvinces.filter(
      (selection) => !countryLevelFilters.includes(selection)
    );

    // Now, filter the stores with clear, separate conditions.
    return this.allStores.filter((store) => {
      // Condition 1: Show the store if its country_name is in our list of selected countries.
      // This works for Namibia, Lesotho, etc.
      if (selectedCountries.includes(store.country_name)) {
        return true;
      }

      // Condition 2: Show the store if its province is in our list of selected SA provinces.
      if (selectedSaProvinces.includes(store.province)) {
        return true;
      }

      // If neither condition is met, exclude the store.
      return false;
    });
  }

  getFilteredStoresBySelection() {
    return this.getStoresByProvinceAndCountrySelection();
  }

  createStoreListing(store, container) {
    const storeId = `store-${
      store.id || store.name.toLowerCase().replace(/\s+/g, "-")
    }`;
    const storeDiv = document.createElement("div");
    storeDiv.id = storeId;
    storeDiv.classList.add("store-item");

    const distanceText = store.distance
      ? `<p class="store-distance">${this.formatDistance(
          store.distance
        )} away</p>`
      : "";

    // Operating hours formatting for Tekkie Town (simplified - no combined stores)
    const operatingHoursHtml = this.formatOperatingHours(store.operating_hours);

    // Format operational status
    const statusHtml = store.status
      ? `<p><strong>Status:</strong> <span class="store-status ${store.status
          .toLowerCase()
          .replace(/\s+/g, "-")}">${store.status}</span></p>`
      : "";

    storeDiv.innerHTML = `
          <h3>${store.name}</h3>
          ${distanceText}
          ${statusHtml}
          <p><strong>Contact:</strong> ${store.contact_number}</p>
          <p><strong>Address:</strong> <a href="${store.google_maps_url}" target="_blank">${store.address}</a></p>
          ${operatingHoursHtml}
          <button style="background-color: ${this.config.buttonColor};" 
                  class="store-view-btn" 
                  data-lat="${store.latitude}" 
                  data-lng="${store.longitude}">
            View on Map
          </button>
        `;

    storeDiv.querySelector(".store-view-btn").addEventListener("click", () => {
      this.panToStore(parseFloat(store.latitude), parseFloat(store.longitude));
    });

    container.appendChild(storeDiv);
    store.markerElement = storeDiv;
  }

  async createStoreMarker(store) {
    try {
      
      const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

      let markerContent;

      
      const buildCustomMarker = (bgColor, borderColor, glyphUrl, glyphWidth, glyphHeight) => {
        const markerWrapper = document.createElement("div");

        
        // Styles to look like a circular pin.
        markerWrapper.style.width = '30px';
        markerWrapper.style.height = '30px';
        markerWrapper.style.borderRadius = '50%';
        markerWrapper.style.backgroundColor = bgColor;
        markerWrapper.style.border = `2px solid ${borderColor}`;
        markerWrapper.style.display = 'flex';
        markerWrapper.style.justifyContent = 'center';
        markerWrapper.style.alignItems = 'center';
        markerWrapper.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
        
        // The glyph SVG
        const glyphElement = document.createElement("img");
        glyphElement.src = glyphUrl;
        glyphElement.style.width = glyphWidth;
        glyphElement.style.height = glyphHeight;
        
        // Add the glyph inside our custom pin
        markerWrapper.appendChild(glyphElement);
        
        return markerWrapper;
      };

      if (store.storeType === "manual") {
        // We can't use a glyph for the manual one, so let's create a simple colored dot
        const color = this.manualStoreColor;
        markerContent = buildCustomMarker(color, color, '', '0', '0'); // No glyph needed
      } else {
        // Build the custom marker for the SVG stores
        markerContent = buildCustomMarker(
          this.config.svg_map_marker_background_color,
          this.config.svg_map_marker_border_color,
          this.config.svg_map_marker_url,
          this.config.svg_map_marker_width,
          this.config.svg_map_marker_height
        );
      }

      const marker = new AdvancedMarkerElement({
        position: { lat: store.latitude, lng: store.longitude },
        map: this.map,
        title: store.name,
        // The content is now our custom, behavior-free HTML element
        content: markerContent,
        // This is still essential to prevent the marker from being replaced on click
        collisionBehavior: google.maps.CollisionBehavior.REQUIRED,
      });

      // The click listener does not need to change
      marker.addListener("click", async ({ domEvent, latLng }) => {
        domEvent.stopPropagation();
        const store = this.allStores.find(
          (s) =>
            Math.abs(s.latitude - latLng.lat()) < 0.0001 &&
            Math.abs(s.longitude - latLng.lng()) < 0.0001
        );
        if (store) {
          if (this.infoWindow) this.infoWindow.close();
          const { InfoWindow } = await google.maps.importLibrary("maps");
          this.infoWindow = new InfoWindow({ content: this.createInfoWindowContent(store) });
          await this.ensureStoreVisible(store);
          this.infoWindow.open({ anchor: marker, map: this.map });
          await this.scrollToStoreInList(store);
        }
      });

      this.markers.push(marker);
      return marker;
    } catch (error) {
      console.error(`Failed to create marker for ${store.name}:`, error);
      return null;
    }
  }

  async ensureStoreVisible(store) {
    const storeId = `store-${
      store.id || store.name.toLowerCase().replace(/\s+/g, "-")
    }`;
    const storeElement = document.getElementById(storeId);

    if (!storeElement) {
      this.updateVisibleStores();
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  createInfoWindowContent(store) {
    return `
            <div class="info-window">
              <h3 style="font-size: 13px;">${store.name}</h3>
              <a href="${store.google_maps_url}" target="_blank" 
                 style="color: ${this.config.buttonColor};"><strong>
                View on Google Maps</strong>
              </a>
            </div>
          `;
  }

  async scrollToStoreInList(store) {
    const storeId = `store-${
      store.id || store.name.toLowerCase().replace(/\s+/g, "-")
    }`;

    // Wait briefly for DOM updates
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const storeElement = document.getElementById(storeId);
    if (!storeElement) {
      console.warn("Store element not found after waiting");
      return;
    }

    const container = this.showingAllStores
      ? document.getElementById(`all-stores-${this.sectionId}`)
      : document.getElementById(`nearby-stores-${this.sectionId}`);

    if (container) {
      // Calculate position with safety checks
      const elementTop = storeElement.offsetTop;
      const containerHeight = container.offsetHeight;
      const scrollPosition = Math.max(0, elementTop - containerHeight / 3);

      container.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });

      this.highlightStore(storeElement);
    }
  }

  highlightStore(element) {
    // First make sure element is visible
    element.style.display = "block";
    void element.offsetHeight; // Trigger reflow

    // Clear previous highlights
    document.querySelectorAll(".store-item.highlighted").forEach((el) => {
      el.classList.remove("highlighted");
    });

    // Add highlight with animation
    element.classList.add("highlighted");

    // Set timeout to remove highlight
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
    }
    this.highlightTimeout = setTimeout(() => {
      element.classList.remove("highlighted");
    }, 3000);
  }

  formatDistance(distanceInMeters) {
    if (distanceInMeters < 1000) {
      return `${Math.round(distanceInMeters)}m`;
    } else {
      return `${(distanceInMeters / 1000).toFixed(1)}km`;
    }
  }

  formatOperatingHours(hours, contentOnly = false) {
    if (!hours || Object.keys(hours).length === 0) return "";

    const daysOrder = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    const formatTime = (timeStr) => {
      if (!timeStr) return "";
      return timeStr.includes(":") ? timeStr.slice(0, 5) : "";
    };

    // Group consecutive days with same hours
    const groupedHours = [];
    let currentGroup = null;

    daysOrder.forEach((day) => {
      const dayHours = hours[day];
      // Check if the day is closed or hours are not defined
      if (!dayHours || !dayHours.open || !dayHours.close) {
        // If we were in a group, push it to the list and reset
        if (currentGroup) {
          groupedHours.push(currentGroup);
          currentGroup = null;
        }
        return; // Move to the next day
      }

      const openTime = formatTime(dayHours.open);
      const closeTime = formatTime(dayHours.close);
      const timeRange = `${openTime}-${closeTime}`;

      if (!currentGroup) {
        currentGroup = {
          days: [day],
          timeRange: timeRange,
        };
      } else if (currentGroup.timeRange === timeRange) {
        // Same hours, add day to the current group
        currentGroup.days.push(day);
      } else {
        // Different hours, push the old group and start a new one
        groupedHours.push(currentGroup);
        currentGroup = {
          days: [day],
          timeRange: timeRange,
        };
      }
    });

    // Push the last group if it exists
    if (currentGroup) {
      groupedHours.push(currentGroup);
    }

    // Format day ranges (e.g., Mon-Fri)
    const formatDayRange = (days) => {
      if (days.length === 1) {
        return days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      }
      const firstDay = days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      const lastDay =
        days[days.length - 1].charAt(0).toUpperCase() +
        days[days.length - 1].slice(1, 3);
      return `${firstDay}-${lastDay}`;
    };

    let hoursContent = "";
    groupedHours.forEach((group) => {
      if (group.days.length > 0) {
        hoursContent += `<p>${formatDayRange(group.days)}: ${
          group.timeRange
        }</p>`;
      }
    });

    // If no content was generated, return empty
    if (!hoursContent) return "";

    // Return just the <p> tags if contentOnly is true
    if (contentOnly) {
      return hoursContent;
    }

    // Return the full HTML structure
    return `<div class="operating-hours"><strong>Hours:</strong>${hoursContent}</div>`;
  }

  async toggleAllStores(showAll = !this.showingAllStores) {
    this.showingAllStores = showAll;

    const nearbyContainer = document.getElementById(
      `nearby-stores-${this.sectionId}`
    );
    const allContainer = document.getElementById(
      `all-stores-${this.sectionId}`
    );

    if (showAll) {
      nearbyContainer.style.display = "none";
      allContainer.style.display = "block";

      if (allContainer.children.length === 0) {
        this.allStores.forEach((store) => {
          this.createStoreListing(store, allContainer);
        });
      }
    } else {
      nearbyContainer.style.display = "block";
      allContainer.style.display = "none";
    }

    this.updateToggleButton();

    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  updateToggleButton() {
    const toggleBtn = document.getElementById(
      `toggle-all-stores-${this.sectionId}`
    );
    if (toggleBtn) {
      toggleBtn.textContent = this.showingAllStores
        ? "Show Nearby Stores"
        : "Show All Stores";
      toggleBtn.style.display =
        this.nearbyStores.length > 0 ? "section" : "none";
    }
  }

  clearMarkers() {
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
    }
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
  }

  clearStoreListings() {
    const nearbyContainer = document.getElementById(
      `nearby-stores-${this.sectionId}`
    );
    const allContainer = document.getElementById(
      `all-stores-${this.sectionId}`
    );
    if (nearbyContainer) nearbyContainer.innerHTML = "";
    if (allContainer) allContainer.innerHTML = "";
  }

  async searchByAddress() {
    const input = document.getElementById(`autocomplete-${this.sectionId}`);
    const address = input?.value.trim();
    if (!address) return;
    try {
        const geocoder = new google.maps.Geocoder();
        const { results } = await new Promise((resolve, reject) => {
            geocoder.geocode({ address, componentRestrictions: { country: "za" } }, (results, status) => {
                if (status === "OK" && results.length > 0) resolve({ results });
                else reject(new Error(`Geocode error: ${status}`));
            });
        });
        const position = { lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() };
        await this.handleLocationUpdate(position, true); 
    } catch (error) {
        console.error("Search failed:", error);
        alert("Could not find that location. Please try a different address.");
    }
  }


  fallbackGeocode(address) {
    if (!window.google || !google.maps || !google.maps.Geocoder) {
      console.error("Geocoder not available");
      return;
    }

    new google.maps.Geocoder().geocode({ address }, (results, status) => {
      if (status === "OK" && results[0]?.geometry?.location) {
        this.userPosition = {
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        };

        this.map.setCenter(this.userPosition);
        this.map.setZoom(12);

        if (this.searchMarker) {
          this.searchMarker.setMap(null);
        }

        this.searchMarker = new google.maps.marker.AdvancedMarkerElement({
          position: this.userPosition,
          map: this.map,
          title: results[0].formatted_address,
        });

        this.processStores();
      } else {
        console.warn("Geocode was not successful:", status);
        alert(
          "Could not find the location. Please try a more specific address."
        );
      }
    });
  }


   async useMyLocation() {
    if (!navigator.geolocation) {
        return alert("Geolocation is not supported by your browser.");
    }
    
    this.resetProvinceFilter();

    const locationBtn = document.querySelector(`.store-locator-location-btn[data-section-id="${this.sectionId}"]`);
    const originalText = locationBtn.innerHTML;
    
    locationBtn.disabled = true;
    locationBtn.innerHTML = 'Locating...';

    try {
        const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            });
        });
        
        const location = { lat: position.coords.latitude, lng: position.coords.longitude };
        await this.handleLocationUpdate(location, false);
    } catch (error) {
        alert("Could not get your location: " + this.getGeolocationError(error));
    } finally {
        locationBtn.disabled = false;
        locationBtn.innerHTML = originalText;
    }
  }


  getGeolocationError(error) {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return "User denied the request for geolocation.";
      case error.POSITION_UNAVAILABLE:
        return "Location information is unavailable.";
      case error.TIMEOUT:
        return "The request to get user location timed out.";
      case error.UNKNOWN_ERROR:
        return "An unknown error occurred.";
      default:
        return "Geolocation failed.";
    }
  }

  panToStore(lat, lng) {
    if (this.map) {
      this.map.panTo({ lat, lng });
      this.map.setZoom(14);
    }
  }

  showMapError(sectionId, message = "Failed to load map") {
    const mapElement = document.getElementById(`map-${sectionId}`);
    if (mapElement) {
      mapElement.innerHTML = `
              <div class="map-error">
                <p>${message}</p>
                <p>Please try again later or contact support.</p>
              </div>`;
    }
  }

  showStoreLoadError() {
    const container = document.getElementById(
      `results-column-${this.sectionId}`
    );
    if (container) {
      container.innerHTML = `
                  <div class="error-message">
                    <p>Failed to load store locations. Please try again later.</p>
                  </div>
                `;
    }
  }
  setupBrandSelector() {
    const brandSelect = document.getElementById(`brands-${this.sectionId}`);
    if (!brandSelect) return;

    brandSelect.addEventListener("change", (e) => {
      window.storeLocatorConfig[this.sectionId].brand = e.target.value;
      this.refreshStores();
    });
  }

  createClusterMarker(count, color) {
    const { PinElement } = google.maps.marker;
    return new PinElement({
      background: color,
      borderColor: color,
      glyphColor: "#ffffff",
      scale: 1.4,
      glyph: count.toString(),
    });
  }

  refreshCluster() {
    if (this.markerCluster) this.markerCluster.clearMarkers();
    this.markerCluster = new markerClusterer.MarkerClusterer({
      map: this.map,
      markers: this.markers,
      algorithm: new markerClusterer.SuperClusterAlgorithm({
        radius: 100,
        maxZoom: 15,
      }),
      renderer: {
        render: ({ count, position }) =>
          new google.maps.marker.AdvancedMarkerElement({
            position,
            content: this.createClusterMarker(
              count,
              this.config.buttonColor || "#082930"
            ).element,
          }),
      },
    });
  }
}

document.addEventListener("DOMContentLoaded", () => {
  Object.keys(window.storeLocatorConfig).forEach((sectionId) => {
    try {
      new StoreLocator(sectionId, window.storeLocatorConfig[sectionId]);
    } catch (error) {
      console.error(
        `Failed to initialize store locator for section ${sectionId}:`,
        error
      );
    }
  });
});